import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';

const galleryImages = [
  { id: 1,  src: "/gallery/real-action-engine.jpg",      title: "Engine Bay Blasting",        category: "Fleet",       tag: "Real Job" },
  { id: 2,  src: "/gallery/real-action-tire.jpg",        title: "Wheel & Tire Cleaning",      category: "Fleet",       tag: "Real Job" },
  { id: 3,  src: "/gallery/real-axle-closeup.jpg",       title: "Axle & Undercarriage",       category: "Fleet",       tag: "Real Job" },
  { id: 4,  src: "/gallery/real-before-grime.jpg",       title: "Heavy Grime Removal",        category: "Before",      tag: "Real Job" },
  { id: 5,  src: "/gallery/real-truck-front.jpg",        title: "Full Truck Service",         category: "Fleet",       tag: "Real Job" },
  { id: 6,  src: "/gallery/gallery-hood-cleaning.jpg",   title: "Kitchen Hood Cleaning",      category: "Restaurant",  tag: "Service" },
  { id: 7,  src: "/gallery/gallery-truck-undercarriage.jpg", title: "Semi Truck Undercarriage", category: "Fleet",    tag: "Service" },
  { id: 8,  src: "/gallery/gallery-fire-damage.jpg",     title: "Fire & Smoke Restoration",   category: "Restoration", tag: "Service" },
  { id: 9,  src: "/gallery/ai-worker-action.jpg",        title: "Industrial Equipment Clean",  category: "Industrial",  tag: "Service" },
  { id: 10, src: "/gallery/ai-clean-result.jpg",         title: "Spotless Machine Results",   category: "After",       tag: "Results" },
];

const DEMO_VIDEO = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/f5j74oaa_Dry%20Ice%20cleaning%20video.mp4";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const GalleryPage = () => {
  return (
    <main className="bg-black pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl"
          >
            <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
              Our Work
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Project Gallery
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed">
              Real results from real jobs. Dry ice blasting transforms dirty, corroded equipment —
              no water, no chemicals, no teardown.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-black" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
          >
            {galleryImages.map((image) => (
              <motion.div
                key={image.id}
                variants={fadeInUp}
                transition={{ duration: 0.35 }}
                className="gallery-item group relative aspect-[4/3] bg-zinc-950 overflow-hidden"
                data-testid={`gallery-item-${image.id}`}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Orange tag top-left */}
                <div className="absolute top-3 left-3">
                  <span className="bg-orange-600 text-black text-xs font-bold px-2 py-1 uppercase tracking-wider">
                    {image.tag}
                  </span>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="badge bg-orange-600 text-black px-3 py-1 mb-2 inline-block text-xs uppercase tracking-wider">
                    {image.category}
                  </span>
                  <h3 className="text-lg uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {image.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-800" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              Transformations
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Before & After
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              The same equipment, before and after. No water. No chemicals. No disassembly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Pair 1 — Fleet undercarriage */}
            <div className="bg-black border border-zinc-800 p-6" data-testid="before-after-1">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="relative aspect-video overflow-hidden">
                  <img src="/gallery/real-before-grime.jpg" alt="Before - heavy grime and corrosion" className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2">
                    <span className="bg-zinc-800 text-zinc-300 text-xs font-bold px-2 py-1 uppercase">Before</span>
                  </div>
                </div>
                <div className="relative aspect-video overflow-hidden">
                  <img src="/gallery/real-truck-front.jpg" alt="After - clean truck exterior" className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2">
                    <span className="bg-orange-600 text-black text-xs font-bold px-2 py-1 uppercase">After</span>
                  </div>
                </div>
              </div>
              <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Fleet Vehicle — Full Exterior & Underbody
              </h3>
              <p className="text-zinc-400 text-sm">Heavy grease, carbon, and corrosion removed in-place. Zero water. Zero teardown.</p>
            </div>

            {/* Pair 2 — Axle detail */}
            <div className="bg-black border border-zinc-800 p-6" data-testid="before-after-2">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="relative aspect-video overflow-hidden">
                  <img src="/gallery/real-axle-closeup.jpg" alt="Before - corroded axle components" className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2">
                    <span className="bg-zinc-800 text-zinc-300 text-xs font-bold px-2 py-1 uppercase">Before</span>
                  </div>
                </div>
                <div className="relative aspect-video overflow-hidden">
                  <img src="/gallery/ai-clean-result.jpg" alt="After - clean machinery" className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2">
                    <span className="bg-orange-600 text-black text-xs font-bold px-2 py-1 uppercase">After</span>
                  </div>
                </div>
              </div>
              <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Axle & Undercarriage — Detail Clean
              </h3>
              <p className="text-zinc-400 text-sm">Dry ice strips rust scale and packed grime from tight spaces that pressure washing misses.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-black border-t border-zinc-800" data-testid="video-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              See It In Action
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              The 23rd Demo
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Watch our 60-second demo — CO2 pellets stripping grime in real time. No water. No mess.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video bg-zinc-950 border border-zinc-800 overflow-hidden">
              <video
                src={DEMO_VIDEO}
                controls
                playsInline
                poster="/gallery/real-action-engine.jpg"
                className="w-full h-full object-contain"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-zinc-500 text-sm mt-4 font-mono">
              Follow us: #dryiceblasting · #dryicecleaning · #23rdindustries
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-black mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Want To See This On Your Equipment?
          </h2>
          <p className="text-black/80 text-lg mb-8 max-w-2xl mx-auto">
            We demonstrate on one unit at no charge. You see the results before you commit to anything.
          </p>
          <Link
            to="/contact"
            className="bg-black text-white px-10 py-4 text-lg uppercase tracking-widest inline-flex items-center gap-2 hover:bg-zinc-900 transition-colors"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="gallery-cta"
          >
            Book a Free Demo
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
